import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type QueryKey,
  useSuspenseInfiniteQuery,
  type InfiniteData,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {parseLinkHeader} from '../../utils/parse-link-header'
import type {IssueCommentsPayload} from '../payloads/issue-comments'
import {useIssueCommentsActions, useIssueComments} from '../../stores/selectors'

export function issueCommentsQueryKey(pathName: string): QueryKey {
  return [PageData.issueComments, pathName]
}

export type IssueCommentsPage = {
  data: IssueCommentsPayload
  nextUrl: string | null
}

export type IssueCommentsPaginatedData = {
  pages: IssueCommentsPage[]
  pageParams: string[]
}

export function issueCommentsApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.issueComments}`
}

/**
 * Hook for fetching issue comments with pagination support.
 * Returns the infinite query result directly, allowing consumers to manage pagination.
 *
 * The API will only return a link to the next page if there are more pages to fetch and pagination is enabled.
 *
 * @param pathName - The base path for the API request
 * @returns The infinite query result with pagination controls
 */
export function useInfiniteIssueCommentsPageData({
  pathName,
}: {
  pathName: string
}): UseSuspenseInfiniteQueryResult<InfiniteData<IssueCommentsPage>> {
  const issuesData = useIssueComments()
  const {setIssueComments} = useIssueCommentsActions()
  return useSuspenseInfiniteQuery<IssueCommentsPage, Error, InfiniteData<IssueCommentsPage>, QueryKey, string>({
    queryKey: issueCommentsQueryKey(pathName),
    initialPageParam: issueCommentsApiUrl(pathName),
    queryFn: async ({pageParam}: {pageParam: string}): Promise<IssueCommentsPage> => {
      const result = await reactFetch(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch issue comments from ${pageParam}: HTTP ${result.status}`)

      const json = await result.json()
      const linkHeader = result.headers.get('Link')

      const nextUrl = parseLinkHeader(linkHeader, 'next')

      const data = json as IssueCommentsPayload

      setIssueComments([...issuesData, ...data])
      return {data, nextUrl}
    },
    getNextPageParam: (lastPage: IssueCommentsPage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
  })
}
